import type { AuthUser } from 'aws-amplify/auth';

import type { LoadUserPropertiesResult } from '@/models/auth.ts';
import { LoadUserPropertiesStatus } from '@/models/auth.ts';

import { signOut } from '@/services/auth-service.ts';
import { showErrorNotification } from '@/services/notification-service.ts';

import { useAuthStore } from '@/stores/auth-store.ts';
import { useConfigStore } from '@/stores/config-store.ts';
import { useUIStore } from '@/stores/ui-store.ts';

import { deriveUserProperties } from '@/utils/auth-utils.ts';

const handleAuthResult = async (
    result: LoadUserPropertiesResult,
    user?: AuthUser
): Promise<LoadUserPropertiesStatus> => {
    switch (result.status) {
        case LoadUserPropertiesStatus.INVALID: {
            showErrorNotification('Ihre Sitzung konnte nicht verifiziert werden. Sie wurden abgemeldet.');
            useUIStore.setState({ isFancyCheckinRendered: false });
            try {
                await signOut();
            } catch (signOutError) {
                console.error('Failed to sign out after invalid token:', signOutError);
            } finally {
                useAuthStore.getState().reset();
                useConfigStore.getState().reset();
            }
            break;
        }
        case LoadUserPropertiesStatus.NO_SESSION: {
            useAuthStore.getState().reset();
            useConfigStore.getState().reset();
            useUIStore.setState({ isFancyCheckinRendered: false });
            break;
        }
        case LoadUserPropertiesStatus.SUCCESS: {
            const { isFancyCheckinRendered, ...authProps } = deriveUserProperties(result.data);
            useAuthStore.setState({ ...authProps, user });
            useUIStore.setState({ isFancyCheckinRendered });
            break;
        }
        default: {
            break;
        }
    }

    return result.status;
};

interface AuthResultHandler {
    handleAuthResult: typeof handleAuthResult;
}

export const useAuthResultHandler = (): AuthResultHandler => {
    return { handleAuthResult };
};
